import React, { useMemo, useRef } from 'react';
import classNames from 'classnames';
import styles from './css/index.module.scss';
import Checkbox from '../../../../ui-kit/Checkbox';
import Button from '../../../../ui-kit/Button';
import Loading from '../../../../ui-kit/Loading';
import Pagination from '../../../../ui-kit/Pagination';
import CompactSelect from '../CompactSelect';
import QuestionHint, { QUESTION_HINT_POSITIONS } from '../../../../components/QuestionHint';
import RwLabel from '../RwLabel';
import ResultsState, { RESULTS_STATES } from '../ResultsState';
import useSoundproofing from '../../../../hooks/useSoundproofing';
import useTranslations from '../../../../hooks/useTranslations';
import { DICTIONARY } from '../../../../translations/dictionary';
import { SOUNDPROOFING_PAGE_SIZES, SOUNDPROOFING_RANGE_KEYS } from '../../../../constants/soundproofing';

const VALUE_COLUMNS = [
  { key: SOUNDPROOFING_RANGE_KEYS.thickness, field: 'thickness' },
  { key: SOUNDPROOFING_RANGE_KEYS.rw, field: 'rw' },
  { key: SOUNDPROOFING_RANGE_KEYS.rwC, field: 'rwC' },
  { key: SOUNDPROOFING_RANGE_KEYS.rwCtr, field: 'rwCtr' },
];

function ColumnTitle({ columnKey }) {
  const { t } = useTranslations();
  if (columnKey === SOUNDPROOFING_RANGE_KEYS.thickness) return t(DICTIONARY.soundproofingPage.thickness);
  return <RwLabel rangeKey={columnKey} />;
}

function ResultsTable({ isMobile }) {
  const { t } = useTranslations();
  const wrapperRef = useRef(null);
  const {
    results,
    resultsTotal,
    resultsPage,
    resultsPageSize,
    resultsLoading,
    resultsError,
    selectedResultsIds,
    isResultSelectionLimitReached,
    changeResultsPage,
    changeResultsPageSize,
    toggleResultSelection,
    toggleAllResultsOnPage,
    openInConfigurator,
  } = useSoundproofing();

  const pagesCount = Math.max(Math.ceil(resultsTotal / resultsPageSize), 1);
  const isAllOnPageSelected = results.length > 0 && results.every((item) => selectedResultsIds.includes(item.id));

  const pageSizeItems = useMemo(
    () => SOUNDPROOFING_PAGE_SIZES.map((size) => ({ value: size, label: String(size) })),
    [],
  );
  const shownFrom = (resultsPage - 1) * resultsPageSize + 1;
  const shownTo = Math.min(resultsPage * resultsPageSize, resultsTotal);

  const scrollToTop = () => {
    wrapperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const onPageChange = (page) => {
    changeResultsPage(page);
    scrollToTop();
  };

  const onPageSizeChange = (size) => {
    changeResultsPageSize(size);
    scrollToTop();
  };

  if (resultsError && !resultsLoading) return <ResultsState type={RESULTS_STATES.error} />;
  if (!resultsLoading && !resultsTotal) return <ResultsState type={RESULTS_STATES.empty} />;
  if (resultsLoading && !results.length) {
    return <div className={styles.loading}><Loading height={30} width={30} /></div>;
  }

  const renderCheckbox = (item) => {
    const isSelected = selectedResultsIds.includes(item.id);
    return (
      <Checkbox
        value={isSelected}
        disabled={!isSelected && isResultSelectionLimitReached}
        onClick={() => toggleResultSelection(item.id)}
      />
    );
  };

  const renderToConfiguratorButton = (width = null) => (
    <Button
      text={t(DICTIONARY.soundproofingPage.toConfigurator)}
      height={isMobile ? 36 : 28}
      width={width}
      paddingHorizontal={12}
      onClick={() => openInConfigurator()}
    />
  );

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <div className={classNames(styles.table, { [styles.tableLoading]: resultsLoading })}>
        {!isMobile && (
          <div className={classNames(styles.row, styles.headerRow)}>
            <div className={styles.cellCheckbox}>
              <Checkbox value={isAllOnPageSelected} onClick={toggleAllResultsOnPage} />
            </div>
            <div className={classNames(styles.cell, styles.cellComposition)}>
              <p className={styles.headerText}>{t(DICTIONARY.soundproofingPage.composition)}</p>
              <QuestionHint
                text={t(DICTIONARY.soundproofingPage.compositionHint)}
                position={QUESTION_HINT_POSITIONS.bottomRight}
              />
            </div>
            {VALUE_COLUMNS.map(({ key }) => (
              <div key={key} className={classNames(styles.cell, styles.cellValue)}>
                <p className={styles.headerText}><ColumnTitle columnKey={key} /></p>
              </div>
            ))}
            <div className={styles.cellAction} />
          </div>
        )}

        {results.map((item) => {
          const isSelected = selectedResultsIds.includes(item.id);
          const isDisabled = !isSelected && isResultSelectionLimitReached;

          return isMobile ? (
            <div
              key={item.id}
              className={classNames(styles.card, {
                [styles.rowSelected]: isSelected,
                [styles.rowDisabled]: isDisabled,
              })}
            >
              <div className={styles.cardHeader}>
                {renderCheckbox(item)}
                <p className={styles.cardComposition}>{item.composition}</p>
              </div>
              <div className={styles.cardValues}>
                {VALUE_COLUMNS.map(({ key, field }) => (
                  <div key={key} className={styles.cardValue}>
                    <p className={styles.cardValueTitle}><ColumnTitle columnKey={key} /></p>
                    <p className={styles.cardValueText}>{item[field]}</p>
                  </div>
                ))}
              </div>
              {renderToConfiguratorButton('100%')}
            </div>
          ) : (
            <div
              key={item.id}
              className={classNames(styles.row, styles.bodyRow, {
                [styles.rowSelected]: isSelected,
                [styles.rowDisabled]: isDisabled,
              })}
            >
              <div className={styles.cellCheckbox}>{renderCheckbox(item)}</div>
              <div className={classNames(styles.cell, styles.cellComposition)}>
                <p className={styles.cellText}>{item.composition}</p>
              </div>
              {VALUE_COLUMNS.map(({ key, field }) => (
                <div key={key} className={classNames(styles.cell, styles.cellValue)}>
                  <p className={styles.cellText}>{item[field]}</p>
                </div>
              ))}
              <div className={styles.cellAction}>{renderToConfiguratorButton()}</div>
            </div>
          );
        })}
      </div>

      <div className={styles.footer}>
        <div className={styles.shown}>
          <p className={styles.shownText}>{t(DICTIONARY.soundproofingPage.shown)}</p>
          <CompactSelect
            width={84}
            height={32}
            withBorder={false}
            items={pageSizeItems}
            value={resultsPageSize}
            onChange={onPageSizeChange}
            renderValue={() => `${shownFrom}-${shownTo}`}
            className={styles.pageSizeSelect}
          />
          <p className={styles.shownText}>{t(DICTIONARY.soundproofingPage.ofResults, resultsTotal)}</p>
        </div>
        {pagesCount > 1 && (
          <div className={styles.pagination}>
            <Pagination page={resultsPage} setPage={onPageChange} pagesCount={pagesCount} />
          </div>
        )}
      </div>
    </div>
  );
}

export default ResultsTable;
